import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { ChevronLeftIcon, CloseIcon } from "./icons";
import { fetchWarmUpSet } from "./warmUpApi";
import { gradeRange, isQuestionCount } from "./warmUpSets";
import { PrimaryButtonLabel, primaryButtonStyles } from "components/pages/Educator/Dashboard/PrimaryButton";
import {
  FRAME_BORDER,
  GRAY_BORDER,
  Message,
  SecondaryButton,
  TextButton,
  VisuallyHidden,
  WHITE,
  interText,
} from "./styles";

const TITLE_ID = "warm-up-preview-title";
const DESCRIPTION_ID = "warm-up-preview-description";

const DETAIL_LOADING = "loading";
const DETAIL_READY = "ready";
const DETAIL_UNAVAILABLE = "unavailable";
const DETAIL_FAILED = "failed";
const SET_NOT_FOUND = 404;
// The student's question screen caps a question image at this height.
const STUDENT_IMAGE_MAX_HEIGHT = 280;

// The set's questions: question 1 from the list at once (when the caller has
// it), all of them once GET /warm-up-sets/{id} answers. A 404 means the set was deactivated since
// the list loaded; any other failure keeps question 1 and can be retried.
const useSetQuestions = (set) => {
  const [detail, setDetail] = useState({ status: DETAIL_LOADING, questions: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setDetail({ status: DETAIL_LOADING, questions: null });
    fetchWarmUpSet(set.id)
      .then(({ questions }) => active && setDetail({ status: DETAIL_READY, questions }))
      .catch((error) => {
        if (!active) return;
        setDetail({ status: error?.status === SET_NOT_FOUND ? DETAIL_UNAVAILABLE : DETAIL_FAILED, questions: null });
      });
    return () => {
      active = false;
    };
  }, [set.id, attempt]);

  const loaded = detail.status === DETAIL_READY;
  return {
    status: detail.status,
    questions: loaded ? detail.questions : [set.first_question].filter(Boolean),
    total: loaded ? detail.questions.length : set.question_count,
    loaded,
    retry: () => setAttempt((count) => count + 1),
  };
};

const STEP_KEYS = { ArrowLeft: -1, ArrowRight: 1 };

// Which question is shown; Previous / Next / the arrow keys step without
// wrapping. A nav button that disables under focus hands focus to the other.
const useQuestionStepper = (total, loaded) => {
  const [index, setIndex] = useState(0);
  const previousRef = useRef(null);
  const nextRef = useRef(null);
  const regionRef = useRef(null);
  const canStep = { [-1]: index > 0, 1: loaded && index < total - 1 };

  useEffect(() => {
    if (regionRef.current) regionRef.current.scrollTop = 0;
    const pairs = [
      [previousRef.current, nextRef.current],
      [nextRef.current, previousRef.current],
    ];
    pairs.forEach(([button, other]) => {
      if (button && button.disabled && button === document.activeElement) other.focus();
    });
  }, [index]);

  const step = (delta) => {
    if (canStep[delta]) setIndex((current) => current + delta);
  };

  const onKeyDown = (event) => {
    if (STEP_KEYS[event.key]) step(STEP_KEYS[event.key]);
  };

  return { index, canStep, step, onKeyDown, previousRef, nextRef, regionRef };
};

// The question's correct answer as the server sends it to teachers, trimmed;
// "" when it sends none (a server without the field, null, or blank).
const solutionOf = (question) => (typeof question.solution === "string" ? question.solution.trim() : "");

// A question's stored image, under its text as the student sees it. One that
// fails to load is dropped without a message.
const PreviewImage = ({ src }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return <QuestionImage src={src} alt="Question visual" onError={() => setFailed(true)} />;
};

// Without onSelect the preview is read-only (opened from a Warm-Up Groups
// card): no Select This Set, and Cancel reads Close.
export const SetPreview = ({ set, onClose, onSelect }) => {
  const readOnly = !onSelect;
  const { status, questions, total, loaded, retry } = useSetQuestions(set);
  const unavailable = status === DETAIL_UNAVAILABLE;
  const { index, canStep, step, onKeyDown, previousRef, nextRef, regionRef } = useQuestionStepper(total, loaded);
  const question = questions[index];
  const solution = question ? solutionOf(question) : "";
  const waiting = !question && status === DETAIL_LOADING;

  return (
    <Modal
      show
      onHide={onClose}
      restoreFocus={false}
      centered
      dialogAs={PreviewDialog}
      aria-labelledby={TITLE_ID}
      aria-describedby={set.description ? DESCRIPTION_ID : undefined}
    >
      <KeyScope onKeyDown={onKeyDown}>
        <Header>
          <HeaderText>
            <Title id={TITLE_ID}>{set.name}</Title>
            {set.description && <Description id={DESCRIPTION_ID}>{set.description}</Description>}
          </HeaderText>
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            <CloseIcon fill={WHITE} />
          </CloseButton>
        </Header>
        <InfoStrip>
          <strong>Grade Range:</strong>
          <span>{gradeRange(set)}</span>
        </InfoStrip>
        <Content>
          <NavRow>
            <NavButton
              type="button"
              ref={previousRef}
              aria-label="Previous question"
              disabled={!canStep[-1]}
              onClick={() => step(-1)}
            >
              <ChevronLeftIcon stroke={WHITE} />
            </NavButton>
            <Position aria-live="polite">
              <span>Question</span>
              {isQuestionCount(total) && <span>{`${index + 1} of ${total}`}</span>}
            </Position>
            <NavButton
              type="button"
              ref={nextRef}
              aria-label="Next question"
              disabled={!canStep[1]}
              onClick={() => step(1)}
              $mirrored
            >
              <ChevronLeftIcon stroke={WHITE} />
            </NavButton>
          </NavRow>
          <QuestionRegion ref={regionRef}>
            {waiting && <VisuallyHidden role="status">Loading questions</VisuallyHidden>}
            {unavailable && (
              <Message role="alert">
                {readOnly
                  ? "This warm-up set is no longer available."
                  : "This warm-up set is no longer available. Pick another one."}
              </Message>
            )}
            {!unavailable && question && (
              <QuestionCard>
                <ProblemText>{question.prompt}</ProblemText>
                {question.internal_image_url && (
                  <PreviewImage key={question.internal_image_url} src={question.internal_image_url} />
                )}
                {solution && <VisuallyHidden>{`Correct answer: ${solution}`}</VisuallyHidden>}
                <AnswerRow aria-hidden="true" inert="">
                  <AnswerBox>{solution}</AnswerBox>
                  <SubmitPill>
                    <span>Submit</span>
                  </SubmitPill>
                </AnswerRow>
              </QuestionCard>
            )}
            {status === DETAIL_FAILED && (
              <DetailError>
                <Message role="alert">
                  {question ? "We couldn't load the other questions." : "We couldn't load the questions."}
                </Message>
                <TextButton type="button" onClick={retry}>
                  <span>Try Again</span>
                </TextButton>
              </DetailError>
            )}
          </QuestionRegion>
          <ButtonsRow>
            <SecondaryButton type="button" onClick={onClose}>
              <span>{readOnly ? "Close" : "Cancel"}</span>
            </SecondaryButton>
            {!readOnly && (
              <SelectButton type="button" disabled={unavailable} onClick={() => onSelect(set)}>
                <PrimaryButtonLabel>Select This Set</PrimaryButtonLabel>
              </SelectButton>
            )}
          </ButtonsRow>
        </Content>
      </KeyScope>
    </Modal>
  );
};

const KeyScope = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

const PreviewDialog = styled(ModalDialog)`
  &.modal-dialog {
    --bs-modal-width: 1004px;
  }

  & .modal-content {
    border: 2px solid ${FRAME_BORDER};
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 56px);
    overflow: hidden;
  }
`;

const Header = styled.div`
  align-items: flex-start;
  flex-shrink: 0;
  background: ${colors.nightlyWoods500};
  display: flex;
  gap: 24px;
  padding: 16px;
`;

const HeaderText = styled.div`
  color: ${colors.milkGlass500};
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Title = styled.h2`
  color: ${colors.milkGlass500};
  font-family: "ABC Marfa", sans-serif;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 32px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Description = styled.p`
  ${interText(16, 22)}
  color: ${colors.milkGlass500};
  margin: 0;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: 2px solid ${WHITE};
  border-radius: 50%;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 48px;
  justify-content: center;
  padding: 0;
  width: 48px;

  &:focus-visible {
    outline: 2px solid ${colors.milkGlass500};
    outline-offset: 2px;
  }
`;

const InfoStrip = styled.div`
  background: ${WHITE};
  flex-shrink: 0;
  display: flex;
  gap: 4px;
  padding: 16px;

  & > strong {
    ${interText(16, 22, 700)}
  }

  & > span {
    ${interText(16, 22)}
  }
`;

// 656 tall as in Figma; on short screens it shrinks (down to 320) so the whole
// dialog fits the viewport, and only the question region scrolls.
const Content = styled.div`
  background: ${colors.milkGlass500};
  box-sizing: border-box;
  display: flex;
  flex: 0 1 656px;
  flex-direction: column;
  gap: 24px;
  min-height: 320px;
  padding: 32px;
`;

const NavRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const NavButton = styled.button`
  align-items: center;
  background: ${colors.nightlyWoods500};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  padding: 0;
  width: 32px;

  & > svg {
    transform: ${({ $mirrored }) => ($mirrored ? "scaleX(-1)" : "none")};
  }

  &:disabled {
    background: ${colors.gray700};
    cursor: default;
  }
`;

// Pinned: Chrome's Inter renders "Question" 62px wide against the design's 63,
// which would move Previous 1px right.
const Position = styled.div`
  ${interText(14, 20, 700, "0")}
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  text-align: center;
  width: 63px;
`;

const QuestionRegion = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  overflow-y: auto;
`;

const QuestionCard = styled.article`
  background: ${WHITE};
  border: 1px solid ${colors.gray700};
  border-radius: 6px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 100%;
  padding: 15px;
  width: 800px;
`;

const ProblemText = styled.p`
  ${interText(16, 22)}
  margin: 0;
`;

const QuestionImage = styled.img`
  align-self: center;
  max-height: ${STUDENT_IMAGE_MAX_HEIGHT}px;
  max-width: 100%;
`;

const AnswerRow = styled.div`
  align-items: center;
  display: flex;
  gap: 24px;
  justify-content: center;
  pointer-events: none;
`;

// Hugs the answer on one line: 76x44 around a single digit, wider for longer
// answers, never wrapped or clipped.
const AnswerBox = styled.div`
  ${interText(18, 26)}
  background: ${WHITE};
  border: 1px solid ${GRAY_BORDER};
  border-radius: 4px;
  box-sizing: border-box;
  flex-shrink: 0;
  height: 44px;
  min-width: 76px;
  padding: 8px 32px;
  text-align: center;
  white-space: nowrap;
`;

// The design's pill frames its label in 4px like the outlined buttons; the
// width is pinned because Chrome's Inter runs about 2px narrower than the
// design's for this label (as with Select This Set).
const SubmitPill = styled.div`
  ${interText(18, 26, 700)}
  align-items: center;
  background: ${WHITE};
  border: 1px solid ${colors.blue500};
  border-radius: 9999px;
  box-sizing: border-box;
  color: ${colors.blue500};
  display: flex;
  height: 48px;
  justify-content: center;
  padding: 0 15px;
  width: 106px;

  & > span {
    padding: 0 4px;
    white-space: nowrap;
  }
`;

const DetailError = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;

  & > button {
    align-self: center;
  }
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 24px;
  justify-content: flex-end;
`;

// Pinned: Chrome's Inter metrics give 182.87px for the design's 185.
const SelectButton = styled.button`
  ${primaryButtonStyles}
  display: inline-flex;
  justify-content: center;
  padding: 0 16px;
  width: 185px;

  &:disabled {
    background: ${colors.gray700};
    cursor: default;
  }
`;
